// Shared projects (PROTOCOL.md §10): thin calls for exchange keys, share invites, accepting, members, leaving and key
// rotation. No crypto here: wraps, sealed keys and safety codes are made and opened by the caller (shared/members.ts).
// A project id here is the owner's own id; shared projects are addressed as `<owner>/<project>` (`projectRef`).
import type { Api } from './api.ts';

export type ShareRole = 'cowriter' | 'commenter';

/** `<owner>/<project>` for a shared project's paths (§10.3); the owner's own project needs only its id. */
export const projectRef = (owner: string, projectId: string) => `${encodeURIComponent(owner)}/${encodeURIComponent(projectId)}`;

const projectPath = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}`;

// Exchange keys (§10.2) ----------------------------------------------------------------------------------------------

export interface StoredExchangeKey {
  /** b64u 65-byte uncompressed P-256 point. */
  publicKey: string;
  /** b64u 60 bytes: the private scalar sealed under the AVK. */
  sealedPrivate: string;
  createdAt?: number;
  updatedAt?: number;
}

/** This account's exchange key; throws ApiError 404 `noExchangeKey` when none is set. */
export const getExchangeKey = (api: Api) => api.get<StoredExchangeKey>('/api/account/exchange-key');

/**
 * Sets the account's exchange key (once; the same values again are fine). Replacing it needs a passkey assertion from
 * `exchangeKeyOptions` passed as `challengeId` and `response`, else `409 exchangeKeySet`.
 */
export async function putExchangeKey(api: Api, key: { publicKey: string; sealedPrivate: string; challengeId?: string; response?: unknown }): Promise<void> {
  await api.call('PUT', '/api/account/exchange-key', key);
}

/** A passkey assertion's options, for replacing the exchange key. */
export const exchangeKeyOptions = (api: Api) => api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/account/exchange-key/options', {});

// Share invites (§10.4, owner) ---------------------------------------------------------------------------------------

export interface NewShare {
  shareId: string;
  /** Shown once: the relay keeps only its hash. */
  code: string;
  /** `APP_ORIGIN/share#<code>`. */
  link: string;
  expiresAt: number;
}

export interface ShareInvite {
  shareId: string;
  role: ShareRole;
  note: string | null;
  createdAt: number;
  expiresAt: number;
  acceptedAt?: number;
  acceptedBy?: string;
  acceptedByName?: string;
  revokedAt?: number;
}

/** (owner) A share invite; `409 membersFull` `{role, limit, used}` when the role is full, `409 noExchangeKey` without one. */
export const createShare = (api: Api, projectId: string, o: { role: ShareRole; note?: string; expiresInDays?: number }) =>
  api.call<NewShare>('POST', `${projectPath(projectId)}/shares`, o);

/** (owner) The project's share invites, newest first. */
export async function listShareInvites(api: Api, projectId: string): Promise<ShareInvite[]> {
  return (await api.get<{ shares: ShareInvite[] }>(`${projectPath(projectId)}/shares`)).shares;
}

/** (owner) Revokes an unaccepted share invite. */
export async function revokeShare(api: Api, projectId: string, shareId: string): Promise<void> {
  await api.call('DELETE', `${projectPath(projectId)}/shares/${encodeURIComponent(shareId)}`);
}

// Accepting and the member's side (§10.4–10.5) -----------------------------------------------------------------------

export interface AcceptedShare {
  owner: string;
  projectId: string;
  role: ShareRole;
  ownerName: string;
  /** For the safety code (null if the owner has none, which the relay doesn't let happen for a new share). */
  ownerPublicKey: string | null;
  status: 'pending';
}

/** Accepts a share code (or a whole `/share#…` link): `403 shareInvalid`, `409 ownShare`, `409 alreadyMember`, `409 noExchangeKey`. */
export const acceptShare = (api: Api, code: string) => api.call<AcceptedShare>('POST', '/api/shares/accept', { code });

export interface Share {
  owner: string;
  ownerName: string | null;
  ownerPublicKey: string | null;
  projectId: string;
  role: ShareRole;
  status: 'pending' | 'active' | 'removed' | 'deleted';
  /** While active: the project key wrapped to this account's exchange key (125 bytes b64u). */
  memberWrap?: string;
  /** While active, once stored: the project key wrapped under this account's AVK (60 bytes b64u). */
  avkWrap?: string;
  keyFingerprint: string | null;
  /** While active: sealed with the project key. */
  summary: string | null;
  summaryUpdatedAt: number | null;
  invitedAt: number;
  joinedAt?: number;
}

/** The projects shared with this account. */
export async function listShares(api: Api): Promise<Share[]> {
  return (await api.get<{ shares: Share[] }>('/api/shares')).shares;
}

/** Stores the member's re-wrap of the project key under their own AVK (`409 keyMismatch` if the key moved on). */
export async function storeShareKey(api: Api, owner: string, projectId: string, avkWrap: string, keyFingerprint?: string): Promise<void> {
  await api.call('PUT', `/api/shares/${projectRef(owner, projectId)}/key`, { avkWrap, ...(keyFingerprint ? { keyFingerprint } : {}) });
}

/** Leaves a shared project. */
export async function leaveShare(api: Api, owner: string, projectId: string): Promise<void> {
  await api.call('DELETE', `/api/shares/${projectRef(owner, projectId)}`);
}

// Members (§10.5–10.6, owner) ----------------------------------------------------------------------------------------

export interface Member {
  account: string;
  name: string | null;
  role: ShareRole;
  status: 'pending' | 'active' | 'removed';
  /** The member's exchange public key, for the safety code. */
  publicKey: string | null;
  invitedAt: number;
  joinedAt?: number;
  removedAt?: number;
  /** Removed by leaving. */
  left?: boolean;
}

export interface MembersInfo {
  members: Member[];
  keyFingerprint: string;
  needsRotation: boolean;
  limits: { cowriters: number; commenters: number };
}

/** (owner) The project's members (removed ones for 30 days), its key fingerprint and whether it needs a rotation. */
export const listMembers = (api: Api, projectId: string) => api.get<MembersInfo>(`${projectPath(projectId)}/members`);

/** (owner) Confirms a pending member (after the safety code) with the project key wrapped to them. */
export async function confirmMember(api: Api, projectId: string, account: string, o: { role: ShareRole; wrappedKey: string; keyFingerprint: string }): Promise<void> {
  await api.call('PUT', `${projectPath(projectId)}/members/${encodeURIComponent(account)}`, o);
}

/** (owner) A member's new role. */
export async function setMemberRole(api: Api, projectId: string, account: string, role: ShareRole): Promise<void> {
  await api.call('PATCH', `${projectPath(projectId)}/members/${encodeURIComponent(account)}`, { role });
}

/** (owner) Removes a member at once (their sockets close with 4004; the project then needs a rotation). */
export async function removeMember(api: Api, projectId: string, account: string): Promise<void> {
  await api.call('DELETE', `${projectPath(projectId)}/members/${encodeURIComponent(account)}`);
}

/** (owner) Records a new project key: the owner's AVK wrap and a member wrap for every active member. */
export async function rotateProject(api: Api, projectId: string, o: { newFingerprint: string; ownerWrap: string; memberWraps: Record<string, string> }): Promise<void> {
  await api.call('POST', `${projectPath(projectId)}/rotate`, o);
}

/** Socket close codes for shared projects (§10.6). */
export const CLOSE_MEMBER_REMOVED = 4004;
